import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { useGetInvestmentFundQuery, useUpdateInvestmentFundMutation } from 'api/main';
import { checkImageFile, getFileExtension, validateFileSize } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import FundFile from './FundFile';
import { ActionButton, Loader, SuccessMessage } from 'components';
import { Box } from '@mui/material';

const containerWidth = { lg: '50%', md: '100%', xl: '35%' };

const initForm = () => ({
    id: '',
    distributorArgument: '',
    regulatoryPack: '',
    advisorPresentation: '',
    brochureAMF: '',
    customerPresentation: '',
    distributorImage: '',
    cashflow: '',
});

const FundDocuments = ({ fundId }) => {
    const {
        data: fund,
        isLoading: fundLoading,
        refetch,
    } = useGetInvestmentFundQuery(fundId, { skip: !fundId });
    const [updateInvestmentFund, { isLoading, isSuccess }] = useUpdateInvestmentFundMutation();

    const [form, setForm] = useState(initForm());
    const [fileSizeErrors, setFileSizeErrors] = useState(initForm());
    const [fileTypeErrors, setFileTypeErrors] = useState(initForm());
    const [successfullyUpdated, setSuccessfullyUpdated] = useState(false);

    const handleFileChange = useCallback(
        key => e => {
            const file = e.target.files[0];
            const fileExtension = getFileExtension(file);

            if (!validateFileSize(file)) {
                // set file size error
                setFileSizeErrors(prevState => ({ ...prevState, [key]: texts.FILE_TOO_LARGE }));
                return;
            } else {
                // clear file size error
                setFileSizeErrors(prevState => ({ ...prevState, [key]: '' }));
            }
            let hasFileTypeError = true;
            if (key === 'distributorImage') {
                checkImageFile(file, () => {
                    hasFileTypeError = false;
                });
            } else if (
                (key === 'regulatoryPack' && fileExtension === 'zip') ||
                (['customerPresentation', 'advisorPresentation'].includes(key) &&
                    ['zip', 'pdf'].includes(fileExtension))
            ) {
                hasFileTypeError = false;
            } else if (key === 'cashflow') {
                // file must be an excel file
                hasFileTypeError = !(
                    fileExtension === 'xls' ||
                    fileExtension === 'csv' ||
                    fileExtension === 'xlsx'
                );
            } else if (fileExtension === 'pdf') {
                hasFileTypeError = false;
            }

            if (hasFileTypeError) {
                setFileTypeErrors(prevState => ({
                    ...prevState,
                    [key]: texts.FILE_TYPE_INCORRECT,
                }));
            } else {
                setFileTypeErrors(prevState => ({ ...prevState, [key]: '' }));
                setForm(prevState => ({ ...prevState, [key]: file }));
            }
        },
        []
    );
    const handleFileDelete = useCallback(
        key => () => {
            setFileTypeErrors(prevState => ({ ...prevState, [key]: '' }));
            setForm(prevState => ({ ...prevState, [key]: null }));
        },
        []
    );
    const changesMade = useMemo(
        () =>
            Object.keys(form).find(
                key =>
                    (key !== 'id' && form[key] && !form?.[key]?.id) || (fund?.[key] && !form[key])
            ),
        [form, fund]
    );

    const handleUpdateFundDocuments = useCallback(() => {
        updateInvestmentFund({
            ...form,
            id: fund?.id,
            distributorArgumentId: fund?.distributorArgument?.id,
            regulatoryPackId: fund?.regulatoryPack?.id,
            advisorPresentationId: fund?.advisorPresentation?.id,
            brochureAMFId: fund?.brochureAMF?.id,
            customerPresentationId: fund?.customerPresentation?.id,
            distributorImageId: fund?.distributorImage?.id,
            cashflowId: fund?.cashflow?.id,
            documentsOnly: true,
        });
    }, [form, fund, updateInvestmentFund]);

    useEffect(() => {
        if (isSuccess) {
            refetch();
            setSuccessfullyUpdated(true);
        }
    }, [isSuccess, refetch]);

    useEffect(() => {
        setForm({
            id: fund?.id,
            distributorArgument: fund?.distributorArgument || '',
            regulatoryPack: fund?.regulatoryPack || '',
            advisorPresentation: fund?.advisorPresentation,
            brochureAMF: fund?.brochureAMF,
            customerPresentation: fund?.customerPresentation,
            distributorImage: fund?.distributorImage || '',
            cashflow: fund?.cashflow || '',
        });
    }, [fund]);

    return (
        <Loader loading={fundLoading}>
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                sx={{ width: containerWidth, marginX: 'auto', gap: 2, paddingY: 3 }}
            >
                <FundFile
                    id="dist-arg"
                    onChange={handleFileChange('distributorArgument')}
                    onDelete={handleFileDelete('distributorArgument')}
                    name={form.distributorArgument?.name || texts.CONSULTANT_ARGUMENT}
                    label={texts.CONSULTANT_ARGUMENT}
                    typeError={!!fileTypeErrors.distributorArgument}
                    sizeError={!!fileSizeErrors.distributorArgument}
                    file={form.distributorArgument}
                />
                <FundFile
                    id="reg-pack"
                    acceptedFileTypes=".zip"
                    onChange={handleFileChange('regulatoryPack')}
                    onDelete={handleFileDelete('regulatoryPack')}
                    name={form.regulatoryPack?.name || texts.REGULATORY_PACK}
                    label={texts.REGULATORY_PACK}
                    typeError={!!fileTypeErrors.regulatoryPack}
                    sizeError={!!fileSizeErrors.regulatoryPack}
                    file={form.regulatoryPack}
                />
                <FundFile
                    id="adv-pres"
                    acceptedFileTypes=".pdf, .zip"
                    onChange={handleFileChange('advisorPresentation')}
                    onDelete={handleFileDelete('advisorPresentation')}
                    name={form.advisorPresentation?.name || texts.ADVISOR_PRESENTATION}
                    label={texts.ADVISOR_PRESENTATION}
                    typeError={!!fileTypeErrors.advisorPresentation}
                    sizeError={!!fileSizeErrors.advisorPresentation}
                    file={form.advisorPresentation}
                />
                <FundFile
                    id="broc-amf"
                    acceptedFileTypes=".pdf"
                    onChange={handleFileChange('brochureAMF')}
                    onDelete={handleFileDelete('brochureAMF')}
                    name={form.brochureAMF?.name || texts.BROCHURE_AMF}
                    label={texts.BROCHURE_AMF}
                    typeError={!!fileTypeErrors.brochureAMF}
                    sizeError={!!fileSizeErrors.brochureAMF}
                    file={form.brochureAMF}
                />
                <FundFile
                    id="cust-pres"
                    acceptedFileTypes=".pdf, .zip"
                    onChange={handleFileChange('customerPresentation')}
                    onDelete={handleFileDelete('customerPresentation')}
                    name={form.customerPresentation?.name || texts.CUSTOMER_PRESENTATION}
                    label={texts.CUSTOMER_PRESENTATION}
                    typeError={!!fileTypeErrors.customerPresentation}
                    sizeError={!!fileSizeErrors.customerPresentation}
                    file={form.customerPresentation}
                />
                <FundFile
                    id="partner-logo"
                    onChange={handleFileChange('distributorImage')}
                    onDelete={handleFileDelete('distributorImage')}
                    name={form.distributorImage?.name || texts.PARTNER_LOGO}
                    label={texts.PARTNER_LOGO}
                    typeError={!!fileTypeErrors.distributorImage}
                    sizeError={!!fileSizeErrors.distributorImage}
                    file={form.distributorImage}
                />
                <FundFile
                    id="cash-flow"
                    acceptedFileTypes=".csv, .xls, .xlsx"
                    onChange={handleFileChange('cashflow')}
                    onDelete={handleFileDelete('cashflow')}
                    name={form.cashflow?.name || texts.CASHFLOW}
                    label={texts.CASHFLOW}
                    typeError={!!fileTypeErrors.cashflow}
                    sizeError={!!fileSizeErrors.cashflow}
                    file={form.cashflow}
                />
                <ActionButton
                    loading={isLoading}
                    disabled={isLoading || !changesMade}
                    onClick={handleUpdateFundDocuments}
                    variant="contained"
                >
                    {texts.SAVE}
                </ActionButton>
                {successfullyUpdated && <SuccessMessage message={texts.INFO_SAVED} />}
            </Box>
        </Loader>
    );
};

FundDocuments.propTypes = {
    fundId: PropTypes.number.isRequired,
};

export default FundDocuments;
